import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import {
  shelfService,
  iOrderService,
  statusService,
  warehouseService,
  costCenterService,
} from "/app/src/services";
import Box from "/app/src/components/generic/components/box";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { Form, Input, Select, SubmitButton, DatePicker } from "formik-antd";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import HeroTip from "/app/src/components/HeroUi/Tip";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import {
  CostCenter,
  Integration,
  Shelf as ShelfType,
  Warehouse,
} from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { useOrderFields } from "../../settings/fields/orderFieldsData";
import { useTranslation } from "react-i18next";
import { buildParams } from "/app/src/helpers";
import OrderLines from "./orderLines";
import { useAuthState } from "/app/src/contexts/authentication";
import { useNavigate } from "react-router-dom";
import { Moment } from "moment";
import { toast } from "sonner";
import { SearchFilter } from "/app/src/components/generic/components/searchFilter";
import { createManualOrderSchema } from "/app/src/schemas/apps/orderBuilder/manualOrderCreationSchema";
import MaterialInformation from "./orderLine/materialInformation";

interface OrderField {
  name: string;
  values: string;
  required: boolean;
}

interface FormValues {
  directionType: string;
  shelves?: string;
  name: string;
  statusId: number;
  [key: string]: string | number; // Allows dynamic fields from orderFields
}

/**
 * OrderBuilder component renders the order builder user interface.
 * @returns {JSX.Element} The rendered OrderBuilder component.
 */
export default function OrderBuilder({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();
  const { orderFields, orderLineFields } = useOrderFields(integration.id);
  type OrderBuilderLine = {
    id?: number;
    materialName: string;
    quantity: number;
  } & {
    [K in (typeof orderLineFields)[number]["name"]]: string;
  };
  const [orderLines, setOrderLines] = useState<OrderBuilderLine[]>([]);

  const { data: warehouses } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => {
      return warehouseService.getAll();
    },
    initialData: { warehouses: [] },
    select: (data: { warehouses: Warehouse[] }) => {
      return data.warehouses;
    },
  });
  const { data: customStatuses } = useQuery({
    queryKey: ["statuses", "custom", integration.id],
    queryFn: () =>
      statusService
        .getAll(
          buildParams({
            integrationId: integration.id,
            includeDefaults: true,
          }),
        )
        .then((response) => {
          return {
            statuses: response.statuses
              .filter((status) => status.updatableStatus === true)
              .sort((a, b) => Number(a.position) - Number(b.position)),
          };
        }),
    initialData: { statuses: [] },
    select: (data) => data.statuses,
  });

  /**
   * Formats the form values and order lines into the expected data structure.
   * @param {FormValues} values - The form values.
   * @param {OrderBuilderLine[]} lines - The order lines.
   * @returns {object} The formatted data object.
   */
  const formatForm = useCallback(
    (values: FormValues, lines?: OrderBuilderLine[]) => {
      // Filter out empty string values from the values object
      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== ""),
      );
      if (values.directionType === "Replenishment") {
        const data = {
          data: JSON.stringify({
            ...filteredValues,
            shelves: [filteredValues.shelves], // Ensure shelves is an array
          }),
        };
        return data;
      } else {
        const data = {
          data: JSON.stringify({
            // Convert all location types to arrays
            ...filteredValues,
            lines,
          }),
        };
        return data;
      }
    },
    [],
  );

  const getLowestPositionStatus = useCallback(() => {
    return (
      customStatuses.reduce((lowest, status) => {
        //Converts the current status' position string to an integer
        const currentPosition = parseInt(status.position, 10);

        // If there isn't a lowest status yet or current position is lower than lowest position
        // return current status as new lowest
        if (!lowest || currentPosition < parseInt(lowest.position, 10)) {
          return status;
        }
        // Otherwise, return the lowest status found so far
        return lowest;
      }, null)?.id ?? null //Returns the id of the status with the lowest position
    );
  }, [customStatuses]);

  // Gets all shelves
  const { data: shelves } = useQuery({
    queryKey: ["shelves"],
    queryFn: () => {
      return shelfService.getAll();
    },
    initialData: { shelves: [] },
    select: (data: { shelves: ShelfType[] }) => {
      return data.shelves;
    },
  });

  // Gets all cost centers
  const { data: costCenters } = useQuery({
    queryKey: ["costCenters"],
    queryFn: () => {
      return costCenterService.getAll();
    },
    initialData: { cost_centers: [] },
    select: (data: { cost_centers: CostCenter[] }) => data.cost_centers,
    enabled:
      orderFields.find((field) => field.name === "costCenterId") !== undefined,
  });

  const mapCostCenters = useCallback((costCenter: CostCenter) => {
    return (
      <Select.Option key={costCenter.id} value={costCenter.id}>
        {costCenter.name}
      </Select.Option>
    );
  }, []);

  const mapShelves = useCallback((shelf: ShelfType) => {
    const displayText = `${shelf.storageUnitName}-${shelf.carrierName}/${shelf.name}`;
    return (
      <Select.Option key={shelf.id} value={shelf.id}>
        {displayText}
      </Select.Option>
    );
  }, []);

  const mapWarehouses = useCallback((warehouse: Warehouse) => {
    return (
      <Select.Option key={warehouse.id} value={warehouse.name}>
        {warehouse.name}
      </Select.Option>
    );
  }, []);

  // Initialize form values dynamically based on orderFields
  const initialValues = useCallback(() => {
    const values: FormValues = {
      name: "",
      directionType: "Pick",
      shelves: "",
      statusId: getLowestPositionStatus(),
    };
    orderFields.forEach((field: OrderField) => {
      values[field.name] = "";
    });
    return values;
  }, [getLowestPositionStatus, orderFields]);

  const handleSubmit = useCallback(
    (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      if (orderLines.length === 0 && values.directionType !== "Replenishment") {
        toast.error(t("translation:cannot_create_order"));
        setSubmitting(false);
        return null;
      }

      // Take the orderLines and exclude the id from the object
      const orderLinesWithoutId = orderLines.map((line) => {
        const { id: _, ...rest } = line;
        return rest;
      });
      const request = {
        integrationId: integration.id,
        userId: user.id,
        ...(values.type === "Replenishment"
          ? formatForm(values)
          : formatForm(values, orderLinesWithoutId)),
        statusId: values.statusId,
        type: values.directionType,
      };

      return iOrderService
        .createSingle(request)
        .then((response) => {
          if ("order" in response) {
            if (response.order.orderSent) {
              toast.success(t("translation:order_added"));
            } else if (response.order.orderStatus === "Error") {
              toast.error(t("translation:order_failed"));
            } else {
              toast.success(
                `${t("translation:order_draft")} ${response.order.orderStatus}`,
              );
            }
            navigate(
              `/apps/${integration.appId}/integrations/${integration.id}/orders/${response.order.id}`,
            );
          }
        })
        .catch((error) => {
          setOrderLines([]);
          toast.error(error.message);
        });
    },
    [
      formatForm,
      integration.appId,
      integration.id,
      navigate,
      orderLines,
      t,
      user.id,
    ],
  );

  /**
   * Adds a new line to the order.
   * @param {OrderBuilderLine} newLine - The new order line to add.
   */
  const addLine = useCallback(
    (newLine: OrderBuilderLine) => {
      if (orderLines.length === 0) {
        newLine.id = 1;
      } else {
        const id = orderLines[orderLines.length - 1].id;
        newLine.id = id + 1;
      }
      setOrderLines((lines) => [...lines, newLine]);
    },
    [orderLines],
  );

  const updateLine = useCallback(
    (lineId: number, newLine: OrderBuilderLine) => {
      setOrderLines((lines) =>
        lines.map((orderLine) =>
          orderLine.id === lineId ? { ...orderLine, ...newLine } : orderLine,
        ),
      );
    },
    [],
  );

  /**
   * Removes an order line by its identifier.
   * @param {number} lineId - The identifier of the order line to remove.
   */
  /**
   * Removes an order line by its identifier.
   * @param {number} lineId - The identifier of the order line to remove.
   */
  const removeLine = useCallback((lineId: number) => {
    setOrderLines((lines) => lines.filter((c) => c.id !== lineId));
  }, []);

  const createDynamicOrderFieldsForm = useCallback(
    (values: FormValues, setFieldValue, setFieldTouched) => {
      /**
       * Dynamically create an onChange handler for date picker fields.
       * @param {string} fieldName - The name of the form field.
       * @returns {(value: Moment) => void} A function that sets the field value.
       */
      const getOnChangeDatePicker = (fieldName: string) => (value: Moment) => {
        setFieldValue(fieldName, value);
      };

      /**
       * Dynamically create an onBlur handler for date picker fields to mark them as touched.
       * @param {string} fieldName - The name of the form field.
       * @returns {() => void} A function that marks the field as touched when called.
       */
      const setDeadlineTouched = () => {
        return () => {
          setFieldTouched("deadline", true);
        };
      };

      /**
       * Get the display value for a given field name or return a default label.
       * @param {string} fieldName - The name of the field to find.
       * @param {string} defaultLabel - The default label if field not found.
       * @returns {any} The values of the field or the default label.
       */
      const getNameFieldValue = (fieldName: string, defaultLabel) => {
        const field = orderFields.find((f) => f.name === fieldName);
        return field ? field.values : defaultLabel;
      };

      return (
        <>
          <Form.Item
            name="name"
            hasFeedback={false}
            label={getNameFieldValue("name", t("translation:name"))}
          >
            <Input name="name" size="large" />
          </Form.Item>

          <Form.Item
            name="directionType"
            hasFeedback={false}
            label={t("translation:type")}
          >
            <Select name="directionType" size="large">
              <Select.Option key="1" value="Put">
                {t("translation:put")}
              </Select.Option>
              <Select.Option key="2" value="Pick">
                {t("translation:pick")}
              </Select.Option>
              <Select.Option key="3" value="Replenishment">
                {t("translation:replenishment")}
              </Select.Option>
            </Select>
          </Form.Item>

          {values.directionType === "Replenishment" && (
            <Form.Item
              hasFeedback={false}
              name="shelves"
              label={t("translation:shelf")}
            >
              <SearchFilter
                name="shelves"
                mapOptionsFn={mapShelves}
                list={shelves}
              />
            </Form.Item>
          )}
          <Form.Item
            hasFeedback={false}
            name="statusId"
            label={t("translation:status")}
          >
            <Select name="statusId" size="large">
              {customStatuses.map((status) => (
                <Select.Option key={status.id} value={status.id}>
                  {status.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {orderFields
            .filter((field: OrderField) => field.name !== "name")
            .map((field: OrderField) => (
              <Form.Item
                hasFeedback={false}
                key={field.name}
                name={field.name}
                label={field.values}
              >
                {field.name === "warehouseName" ? (
                  <SearchFilter
                    name={field.name}
                    mapOptionsFn={mapWarehouses}
                    list={warehouses}
                    placeholder={t("translation:select_warehouse")}
                  />
                ) : field.name === "deadline" ? (
                  <DatePicker
                    name={field.name}
                    showTime
                    onChange={getOnChangeDatePicker(field.name)}
                    size="large"
                    style={{ width: "100%" }}
                    onBlur={setDeadlineTouched()}
                  />
                ) : field.name === "costCenterId" ? (
                  <SearchFilter
                    name={field.name}
                    mapOptionsFn={mapCostCenters}
                    list={costCenters}
                  />
                ) : (
                  <Input name={field.name} size="large" />
                )}
              </Form.Item>
            ))}
        </>
      );
    },
    [
      costCenters,
      customStatuses,
      mapCostCenters,
      mapShelves,
      mapWarehouses,
      orderFields,
      shelves,
      t,
      warehouses,
    ],
  );

  const createOrderForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid, values, setFieldValue, setFieldTouched }) => (
        <>
          <Box>
            <Form layout="vertical">
              <div className="flex justify-between items-center">
                <h2 className="text-[18px]">
                  {t("translation:order_details")}
                </h2>
                {values.directionType === "Replenishment" ? (
                  <HeroTip content="Generate Lines">
                    <span>
                      <SubmitButton
                        type="primary"
                        disabled={!dirty || !isValid}
                      >
                        {t("translation:generate_order_lines")}
                      </SubmitButton>
                    </span>
                  </HeroTip>
                ) : (
                  <SubmitButton type="primary" disabled={!dirty || !isValid}>
                    {t("translation:create_order")}
                  </SubmitButton>
                )}
              </div>
              <HeroDivider className="my-3" />

              <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
                {createDynamicOrderFieldsForm(
                  values,
                  setFieldValue,
                  setFieldTouched,
                )}
              </GridWrapper>
            </Form>
          </Box>
          <OrderLines
            lines={orderLines}
            addLine={addLine}
            removeLine={removeLine}
            orderLineFields={orderLineFields}
            updateLine={updateLine}
            disabled={values.directionType === "Replenishment"}
          />
        </>
      ),
      [
        t,
        createDynamicOrderFieldsForm,
        orderLines,
        addLine,
        removeLine,
        orderLineFields,
        updateLine,
      ],
    );

  return (
    <div className="app" id="order_builder">
      <Helmet>
        <title>Order Builder - ItemPath</title>
      </Helmet>
      <Formik
        component={createOrderForm}
        initialValues={initialValues()}
        validationSchema={createManualOrderSchema(orderFields)}
        enableReinitialize
        onSubmit={handleSubmit}
      />
      <MaterialInformation integration={integration} />
    </div>
  );
}
